<template lang="pug">
#footer.footer-container.home-footer(:class="{ 'is-zh': locale === 'zh' }")
  .home-footer-inner
    .home-footer-main
      .home-footer-brand
        NuxtLink.home-footer-logo-row(to="/" aria-label="PandaFan")
          span.home-footer-logo-mark(aria-hidden="true")
          span.home-footer-name PandaFan
        p.home-footer-copy {{ homeFooterCopy }}
        p.home-footer-build
          span {{ runtimeConfig.branch }}: {{ runtimeConfig.release }} on {{ hostingLabel }}.
          client-only
            span Built {{ $utils.relativeTime(runtimeConfig.builtDate, false) }}.
            ApiEndpointEditor(v-if="runtimeConfig.isDev && session.realAdmin")
              button.home-footer-admin-switch(type="button") API Endpoint
      nav.home-footer-column(aria-label="Product")
        h2.home-footer-heading {{ locale === 'zh' ? '产品' : 'PRODUCT' }}
        ul.home-footer-list
          li
            NuxtLink.home-footer-link(to="/pricing") {{ $t('header.pricing') }}
          li
            NuxtLink.home-footer-link(to="/download") {{ $t('header.dlandguide') }}
          li
            NuxtLink.home-footer-link(to="/org") {{ $t('header.org') }}
          li
            NuxtLink.home-footer-link(to="/dedicated-servers") {{ locale === 'zh' ? '专线' : 'Dedicated Lines' }}
          li
            NuxtLink.home-footer-link(to="/pandacore") {{ locale === 'zh' ? 'PandaCore 内核' : 'PandaCore Engine' }}
          li
            NuxtLink.home-footer-link(to="/changelog") {{ $t('changelog.nav') }}
      nav.home-footer-column(aria-label="Legal")
        h2.home-footer-heading {{ locale === 'zh' ? '法律' : 'LEGAL' }}
        ul.home-footer-list
          li
            NuxtLink.home-footer-link(to="/privacy-policy") {{ $t('privacy-policy') }}
          li
            NuxtLink.home-footer-link(to="/terms-of-service") {{ $t('tos') }}
      nav.home-footer-column(aria-label="Social")
        h2.home-footer-heading {{ locale === 'zh' ? '社交' : 'SOCIAL' }}
        ul.home-footer-list
          li
            a.home-footer-link(href="https://t.me/pandafan" target="_blank" rel="noopener noreferrer") Telegram
          li
            a.home-footer-link(href="https://twitter.com/pandafannews" target="_blank" rel="noopener noreferrer") Twitter / X
    .home-footer-bottom
      .home-footer-tools
        DarkToggle2
        LangSwitcher.cursor-pointer.text-white
        button.home-footer-admin-switch(
          v-if="session.realAdmin"
          type="button"
          @click="session.switchAdminStatus()"
        ) 切换界面
      p.home-footer-copyright © {{ thisYear }} BEEXIO BV
</template>
<script setup lang="ts">


const thisYear = new Date().getFullYear()

const runtimeConfig = useRuntimeConfig().public
const $utils = useUtils()
const { locale } = useI18n()
const session = useVisitorSession()
const homeFooterCopy = '© 2026 PandaFan from Beexio BV.'

const hostingLabel = computed(() => {
  if (runtimeConfig.nitroPreset === 'cloudflare_module') {
    return `${runtimeConfig.hostingServerName} Worker`
  }
  if (runtimeConfig.nitroPreset === 'cloudflare_pages') {
    return `${runtimeConfig.hostingServerName} Pages`
  }
  return runtimeConfig.hostingServerName
})

</script>

<style lang="postcss">
@reference "@/assets/css/tailwind.css";
#footer {
  @apply relative z-50;
  background: linear-gradient(to bottom,
      color-mix(in oklch, var(--background) 0%, transparent) 0%,
      color-mix(in oklch, var(--background) 50%, transparent) 10%,
      color-mix(in oklch, var(--background) 80%, transparent) 20%,
      rgba(0, 0, 0, 0.85) 30%,
      rgba(0, 0, 0, 0.95) 50%,
      #000000 100%);

  /* Add a subtle overlay for the content area */
  &::before {
    content: '';
    @apply absolute inset-0 pointer-events-none;
    background: linear-gradient(to right,
        rgba(0, 0, 0, 0.2) 0%,
        rgba(31, 41, 55, 0.3) 50%,
        rgba(0, 0, 0, 0.2) 100%);
    z-index: 1;
  }

  /* Ensure content is above the overlay */
  >div {
    @apply relative z-10;
  }

  /* Ensure all text is white */
  a,
  p,
  h5,
  span {
    @apply text-white;
  }

  /* Footer link hover effects */
  .footer-link {
    position: relative;
    overflow: hidden;
    @apply p-0;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 0;
      background: rgb(126, 211, 33);
      transition: height 0.3s ease;
    }

    &:hover {
      background: rgba(126, 211, 33, 0.1);
      color: rgb(126, 211, 33);
      @apply pl-2;

      &::before {
        height: 70%;
      }
    }
  }

  /* Social media link hover effects */
  .social-link {
    border-radius: 0.5rem;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(10px);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg,
          rgba(126, 211, 33, 0) 0%,
          rgba(126, 211, 33, 0.1) 50%,
          rgba(126, 211, 33, 0) 100%);
      transform: translateX(-100%);
      transition: transform 0.6s ease;
    }

    &:hover {
      background: #7ed3210d;

      &::before {
        transform: translateX(100%);
      }

      .social-icon {
        color: rgb(126, 211, 33);
        transform: scale(1.1) rotate(-5deg);
      }

      .underline {
        color: rgb(126, 211, 33);
      }
    }
  }

  /* Admin link - no hover effects */
  .admin-link {
    &:hover {
      transform: none !important;
      color: rgb(59, 130, 246) !important;
      /* Keep blue color */
    }
  }
}

#footer {
  /* border-top: 1px solid var(--secondary-color); */
  /* background: var(--footer-bg); */
  background: linear-gradient(90deg, #000 21px, transparent 1%) 50%, linear-gradient(#000 21px, transparent 1%) 50%,
    #444;
  /* background: var(--background-color); */

  background-size: var(--dot-space) var(--dot-space);
  text-align: center;
  /* padding: 1rem 0; */
  /* --highlight-red: rgb(252, 61, 51); */
  /* --highlight-red: var(--nord-green); */
  --highlight-red: rgb(113, 167, 126);

  @media (max-width: 768px) {
    padding: 0 0 20px;
  }

  .content {
    max-width: var(--primary-max-width);
    margin: 0 auto;
    padding-top: 1rem;
  }

  .color-picker {
    margin-top: 1em;
  }

  vertical-align: middle;


  h5,
  h4,
  h3,
  h2,
  h1 {
    vertical-align: middle;

    font-weight: 500;
    font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Ubuntu, sans-serif;
    color: #ffffff;

    &.hide {
      color: var(--footer-bg);
    }
  }

  h5 {
    font-size: 10px;
  }

  a {
    color: #ffffff;
  }

  img {
    &.invert {
      filter: invert(100%);
    }

    &.logo {
      width: 100%;
      max-width: 20em;
      /* padding-left: 1px; */
    }
  }

  .block {
    cursor: pointer;

    &:hover {
      /* background: var(--highlight-red); */
      transition: all 0.15s ease-out;
    }
  }

  .text {
    padding: 15px;
    vertical-align: top;

    .info {
      h5 {
        line-height: 0.8rem;
        font-weight: 300;
      }

      &.links {
        a {
          transition: all 0.15s ease-out;
          border-left: 1px solid var(--pantone-yellow);
          padding: 2px 2px 2px 10px;
          margin-bottom: 10px;
          display: block;

          &:hover {
            /* padding-left: 15px; */
            /* padding-left: 15px; */
            padding: 2px 2px 2px 15px;
            background-color: var(--highlight-red);
            border: none;
          }
        }
      }
    }
  }

  .block {
    padding: 15px;
    /* margin-top: 10px; */
    font-size: 25px;
    line-height: 25px;
    position: relative;

    p {
      display: block;
      /* border-left: 1px solid var(--pantone-yellow); */
      /* padding: 5px 0 0 20px; */

      .icon {
        position: absolute;
        right: 10px;
        width: 45px;
        top: 20px;
      }

      @media (max-width: 768px) {
        padding-top: 5px;

        .icon {
          width: 35px;
          top: 20px;
        }
      }
    }

    a {
      font-family: Helvetica, sans-serif;
      letter-spacing: 1px;

      span {
        letter-spacing: 0;
      }
    }

    &:hover {
      .small {
        color: #ffffff !important;

        .highlight-underline {
          &:after {
            display: none;
          }
        }
      }
    }

    .small {
      font-size: 15px;
      color: #777;

      @media (max-width: 768px) {
        font-size: 12px;
      }

      .highlight-underline {
        color: #ffffff;
      }
    }
  }
}

.dark #footer {
  background: transparent;
}

.dark #footer::before {
  background: none;
}

#footer.home-footer,
.dark #footer.home-footer {
  padding: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: transparent;
  background-image: none;
  color: #f5f7f1;
  text-align: left;
}

#footer.home-footer::before,
.dark #footer.home-footer::before {
  display: none;
}

#footer.home-footer > .home-footer-inner {
  position: relative;
  z-index: 10;
  width: min(calc(100% - 3rem), 1180px);
  margin-inline: auto;
  padding-block: clamp(1.625rem, 2.7vw, 2.5rem) clamp(1.45rem, 3vw, 2.5rem);
}

#footer.home-footer .home-footer-main {
  display: grid;
  grid-template-columns: minmax(19rem, 1fr) repeat(3, minmax(8.5rem, 12rem));
  column-gap: clamp(3rem, 7vw, 8.25rem);
  row-gap: 2.25rem;
  align-items: start;
}

#footer.home-footer .home-footer-logo-row {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  width: fit-content;
  color: #f5f7f1;
  text-decoration: none;
}

#footer.home-footer .home-footer-logo-mark {
  width: 2.3rem;
  height: 2.3rem;
  flex: none;
  border-radius: 0.55rem;
  background: url(https://alicloud-assets.oss-accelerate.aliyuncs.com/img/logo-color.svg) no-repeat center center;
  background-size: contain;
}

#footer.home-footer .home-footer-name {
  color: #ededed;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0;
}

#footer.home-footer .home-footer-copy {
  max-width: 27rem;
  margin-top: 1rem;
  color: #a1a1a6;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

#footer.home-footer .home-footer-build {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: 27rem;
  margin-top: 0.5rem;
  color: #85858b;
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
}

#footer.home-footer .home-footer-column {
  display: grid;
  align-content: start;
  justify-items: start;
  min-width: 0;
}

#footer.home-footer .home-footer-heading {
  display: flex;
  align-items: center;
  min-height: 2rem;
  margin: 0 0 0.5rem;
  color: #ededed;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
}

#footer.home-footer.is-zh .home-footer-heading {
  letter-spacing: 0;
}

#footer.home-footer .home-footer-list {
  display: grid;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  padding-left: 0;
  padding-inline-start: 0;
  list-style: none;
}

#footer.home-footer .home-footer-list > li {
  padding: 0;
  padding-left: 0;
  padding-inline-start: 0;
}

#footer.home-footer .home-footer-link {
  display: inline-flex;
  width: fit-content;
  padding: 0;
  color: #a1a1a6;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  text-align: left;
  text-decoration: none;
  text-indent: 0;
  transition: color 0.15s cubic-bezier(0.22, 1, 0.36, 1);
}

#footer.home-footer .home-footer-link:hover,
#footer.home-footer .home-footer-link:focus-visible {
  color: #ededed;
}

#footer.home-footer .home-footer-link:focus-visible,
#footer.home-footer .home-footer-logo-row:focus-visible {
  border-radius: 0.25rem;
  outline: 2px solid rgba(126, 211, 33, 0.52);
  outline-offset: 4px;
}

#footer.home-footer .home-footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: clamp(2.5rem, 4.3vw, 4rem);
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

#footer.home-footer .home-footer-tools {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
}

#footer.home-footer .home-footer-tools > * {
  color: #f5f7f1;
}

#footer.home-footer .home-footer-admin-switch {
  border: 0;
  border-radius: 999px;
  padding: 0.35rem 0.65rem;
  background: rgba(245, 247, 241, 0.1);
  color: #f5f7f1;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    transform 0.18s ease;
}

#footer.home-footer .home-footer-admin-switch:hover,
#footer.home-footer .home-footer-admin-switch:focus-visible {
  background: rgba(126, 211, 33, 0.16);
  color: #7ed321;
}

#footer.home-footer .home-footer-admin-switch:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.52);
  outline-offset: 3px;
}

#footer.home-footer .home-footer-copyright {
  color: #85858b;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0;
}

.light #footer.home-footer {
  border-top-color: #e2e8f0;
  color: #1e293b;
}

.light #footer.home-footer .home-footer-logo-row,
.light #footer.home-footer .home-footer-name,
.light #footer.home-footer .home-footer-heading,
.light #footer.home-footer .home-footer-tools > * {
  color: #171717;
}

.light #footer.home-footer .home-footer-copy,
.light #footer.home-footer .home-footer-build,
.light #footer.home-footer .home-footer-copyright {
  color: #737373;
}

.light #footer.home-footer .home-footer-admin-switch {
  background: rgba(15, 23, 42, 0.06);
  color: #1e293b;
}

.light #footer.home-footer .home-footer-admin-switch:hover,
.light #footer.home-footer .home-footer-admin-switch:focus-visible {
  background: rgba(45, 90, 39, 0.1);
  color: #2d5a27;
}

.light #footer.home-footer .home-footer-admin-switch:focus-visible {
  outline-color: rgba(45, 90, 39, 0.52);
}

.light #footer.home-footer .home-footer-link {
  color: #525252;
}

.light #footer.home-footer .home-footer-link:hover,
.light #footer.home-footer .home-footer-link:focus-visible {
  color: #171717;
}

.light #footer.home-footer .home-footer-link:focus-visible,
.light #footer.home-footer .home-footer-logo-row:focus-visible {
  outline-color: rgba(45, 90, 39, 0.52);
}

.light #footer.home-footer .home-footer-bottom {
  border-top-color: #e2e8f0;
}

@media (max-width: 900px) {
  #footer.home-footer > .home-footer-inner {
    width: calc(100% - 2rem);
  }

  #footer.home-footer .home-footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 2.25rem 2rem;
  }

  #footer.home-footer .home-footer-brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 560px) {
  #footer.home-footer .home-footer-main {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }

  #footer.home-footer .home-footer-bottom {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
